import React, { createContext, useState, useContext, ReactNode, useEffect } from "react";
import { getListFilters } from "../../services/restAPI/ListsV2";
import { getSerieSetting } from "../../components/Widgets/TableWidget/TableComponents/TableFilter/TableFIlterScripts";

export interface FilterItem {
   id: string;
   name: string;
   description: string;
   active: boolean;
   filterSections: any[];
   created?: string;
   createdBy?: string;
   updated?: string;
   updatedBy?: string;
}

type TableListContextData = {
   activeFilter: string | boolean;
   setActiveFilter: React.Dispatch<React.SetStateAction<string | boolean>>;
   columnList: any[];
   setColumnList: React.Dispatch<React.SetStateAction<any[]>>;
   rowList: any[];
   setRowList: React.Dispatch<React.SetStateAction<any[]>>;
   widgetData: any;
   setWidgetData: React.Dispatch<React.SetStateAction<any>>;
   listFilters: FilterItem[];
   setListFilters: React.Dispatch<React.SetStateAction<FilterItem[]>>;
   editedListFilters: FilterItem[];
   setEditedListFilters: React.Dispatch<React.SetStateAction<FilterItem[]>>;
   currentActiveFilter: FilterItem | undefined;
   activeTab: number;
   setActiveTab: React.Dispatch<React.SetStateAction<number>>;
   totalElements: number;
   groupsIsChanged: boolean;
   setGroupsIsChanged: React.Dispatch<React.SetStateAction<boolean>>;
   setTotalElements: React.Dispatch<React.SetStateAction<number>>;
   listGroupsForMassActions: FilterItem[];
   setListGroupsForMassActions: React.Dispatch<React.SetStateAction<FilterItem[]>>;
};

const TableListContext = createContext<TableListContextData | undefined>(undefined);

export const TableListProvider = ({
   children,
   initialWidgetData,
}: {
   children: ReactNode;
   initialWidgetData: any;
}) => {
   const [totalElements, setTotalElements] = useState<number>(0);
   const [activeFilter, setActiveFilter] = useState<string | boolean>("");
   const [activeTab, setActiveTab] = useState<number>(1);
   const [columnList, setColumnList] = useState<any[]>([]);
   const [rowList, setRowList] = useState<any[]>([]);
   const [widgetData, setWidgetData] = useState<any>(initialWidgetData);
   const [groupsIsChanged, setGroupsIsChanged] = useState(false);

   const [currentActiveFilter, setCurrentActiveFilter] = useState<FilterItem>();

   const [listFilters, setListFilters] = useState<FilterItem[]>([]);
   const [listGroupsForMassActions, setListGroupsForMassActions] = useState<FilterItem[]>([]);
   const [editedListFilters, setEditedListFilters] = useState<FilterItem[]>([]);

   useEffect(() => {
      if (activeFilter && listFilters?.length > 0) {
         const currentFilter = listFilters?.find(
            (filter) => filter?.id?.toString() === activeFilter?.toString(),
         );

         if (currentFilter) {
            setCurrentActiveFilter(currentFilter);
         }
      } else setCurrentActiveFilter(undefined);
   }, [activeFilter]);

   if (widgetData?.showListFilters) {
      useEffect(() => {
         if (activeTab === 1) {
            getListFilters(getSerieSetting("dataId", widgetData))?.then((res) => {
               setListFilters(res?.data);
               setEditedListFilters(res?.data);
               if (res?.data?.find((filter) => filter?.active === true)) {
                  setActiveFilter(res?.data?.find((filter) => filter?.active === true)?.id);
               } else setActiveFilter(false);
            });
         }
      }, [activeTab]);
   }

   return (
      <TableListContext.Provider
         value={{
            activeFilter,
            setActiveFilter,
            columnList,
            setColumnList,
            rowList,
            setRowList,
            widgetData,
            setWidgetData,
            listFilters,
            setListFilters,
            editedListFilters,
            setEditedListFilters,
            currentActiveFilter,
            activeTab,
            setActiveTab,
            groupsIsChanged,
            setGroupsIsChanged,
            totalElements,
            setTotalElements,
            listGroupsForMassActions,
            setListGroupsForMassActions,
         }}
      >
         {children}
      </TableListContext.Provider>
   );
};

export const useTableListContext = () => {
   const context = useContext(TableListContext);
   if (!context) {
      throw new Error("useTableListContext must be used inside TableListProvider");
   }
   return context;
};
